import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ThemeProvider } from "@mui/material/styles";
import muiTheme, { DIALOG_RADIUS, BUTTON_RADIUS } from "../../../theme/mui/styles";

const PendingApprovalModal = ({
  isOpen,
  onClose,
  approvals,
  loading,
  onApprove,
  approvingVendorId,
}) => {
  const theme = muiTheme;
  const hasApprovals = useMemo(
    () => Array.isArray(approvals) && approvals.length > 0,
    [approvals]
  );

  const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleString();
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={Boolean(isOpen)}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: { borderRadius: DIALOG_RADIUS, mt: 10, alignSelf: "flex-start", maxHeight: "90vh" },
        }}
      >
        <DialogTitle
          sx={{
            pr: 5,
            display: "flex",
            alignItems: "center",
            fontWeight: 700,
            fontSize: 20,
          }}
        >
          Pending Vendor Approvals
          <IconButton
            aria-label="Close"
            onClick={onClose}
            size="small"
            sx={{ ml: "auto" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1, pb: 1 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress color="primary" />
            </Box>
          ) : !hasApprovals ? (
            <Alert severity="info">
              <AlertTitle>No vendors are awaiting approval.</AlertTitle>
              The pending approvals queue is currently empty.
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Raw Name</TableCell>
                  <TableCell>Memo</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {approvals.map((approval) => (
                  <TableRow key={approval.id}>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography fontWeight={600}>
                          {approval.suggested_vendor?.name || "Unnamed Vendor"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {approval.suggested_vendor?.id || "—"}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{approval.raw_name || "—"}</TableCell>
                    <TableCell>{approval.memo || "—"}</TableCell>
                    <TableCell>{formatDate(approval.first_seen_at)}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        color="success"
                        onClick={() =>
                          onApprove({
                            id: approval.suggested_vendor?.id,
                            name: approval.suggested_vendor?.name,
                          })
                        }
                        disabled={!approval.suggested_vendor?.id}
                        sx={{ textTransform: "none" }}
                        size="small"
                      >
                        {approvingVendorId === approval.suggested_vendor?.id
                          ? "Approving..."
                          : "Approve"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={onClose}
            sx={{
              textTransform: "none",
              bgcolor: "#536DEC",
              color: "#fff",
              px: 3.5,
              borderRadius: `${BUTTON_RADIUS}px !important`,
              "&:hover": { bgcolor: "#536DEC" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default PendingApprovalModal;
